import { useState, useEffect, useRef, useCallback } from "react";
import WaitlistForm from "../ui/WaitlistForm";
import TypewriterText from "../ui/TypewriterText";

// ── Hooks ─────────────────────────────────────────────────────────────────────

function useTilt(intensity = 10) {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const ref = useRef(null);

  const onMove = useCallback((e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width  - 0.5;
    const y = (e.clientY - rect.top)  / rect.height - 0.5;
    setTilt({ x: y * -intensity, y: x * intensity });
  }, [intensity]);

  const onLeave = useCallback(() => {
    setTilt({ x: 0, y: 0 });
  }, []);

  return { ref, tilt, onMove, onLeave };
}

function useScrollY() {
  const [y, setY] = useState(0);
  useEffect(() => {
    const fn = () => setY(window.scrollY);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return y;
}

// ── Dashboard mockup data ─────────────────────────────────────────────────────

const HOLDINGS = [
  { ticker: "AAPL", pct: 42, value: "$35,402", color: "#60A5FA" },
  { ticker: "NVDA", pct: 28, value: "$23,601", color: "#10B77F" },
  { ticker: "MSFT", pct: 18, value: "$15,172", color: "#A78BFA" },
  { ticker: "ETFs", pct: 12, value: "$10,115", color: "#FBBF24" },
];

const AI_SIGNALS = [
  { ticker: "AAPL", action: "BUY",  target: "+2.4%", color: "#10B77F" },
  { ticker: "NVDA", action: "BUY",  target: "+5.2%", color: "#10B77F" },
  { ticker: "TSLA", action: "HOLD", target: "+0.8%", color: "#FBBF24" },
];

// ── Dashboard Window ──────────────────────────────────────────────────────────

function DashboardWindow() {
  return (
    <div style={{
      background: "#0A0E1C",
      border: "1px solid rgba(255,255,255,0.09)",
      borderRadius: 18,
      overflow: "hidden",
      boxShadow:
        "0 60px 130px rgba(0,0,0,0.8), " +
        "0 0 0 1px rgba(255,255,255,0.04), " +
        "inset 0 1px 0 rgba(255,255,255,0.07)",
    }}>
      {/* Chrome bar */}
      <div style={{
        padding: "12px 18px",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        display: "flex", alignItems: "center", gap: 10,
        background: "rgba(0,0,0,0.25)",
      }}>
        <div style={{ display: "flex", gap: 7 }}>
          {["#FF5F57", "#FEBC2E", "#28C840"].map(c => (
            <div key={c} style={{ width: 11, height: 11, borderRadius: "50%", background: c, opacity: 0.8 }} />
          ))}
        </div>
        <span style={{
          flex: 1, textAlign: "center",
          fontSize: "0.72rem", color: "rgba(255,255,255,0.28)",
          fontFamily: "Space Grotesk, sans-serif",
        }}>
          AlphaStack — Portfolio Overview
        </span>
        <span style={{
          fontSize: "0.6rem", fontWeight: 700, padding: "2px 8px",
          borderRadius: 5, letterSpacing: "0.05em",
          background: "rgba(16,183,127,0.1)",
          border: "1px solid rgba(16,183,127,0.2)",
          color: "#10B77F", fontFamily: "Space Grotesk, sans-serif",
        }}>
          BETA
        </span>
      </div>

      {/* Body */}
      <div style={{ padding: "18px 20px" }}>

        {/* Value + return */}
        <div style={{ marginBottom: 16 }}>
          <div style={{
            fontSize: "0.6rem", color: "rgba(255,255,255,0.28)",
            fontFamily: "Space Grotesk, sans-serif",
            textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 5,
          }}>
            Portfolio Value
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{
              fontSize: "1.7rem", fontWeight: 800, letterSpacing: "-0.03em",
              color: "#F0EEF6", fontFamily: "Space Grotesk, sans-serif",
              lineHeight: 1,
            }}>
              $84,291
            </span>
            <span style={{
              fontSize: "0.78rem", fontWeight: 700, color: "#10B77F",
              background: "rgba(16,183,127,0.1)",
              border: "1px solid rgba(16,183,127,0.18)",
              padding: "3px 9px", borderRadius: 7,
              fontFamily: "Space Grotesk, sans-serif",
            }}>
              ↑ +18.4%
            </span>
          </div>
        </div>

        {/* Sparkline */}
        <div style={{
          background: "rgba(255,255,255,0.018)",
          border: "1px solid rgba(255,255,255,0.05)",
          borderRadius: 12, padding: "10px 12px",
          marginBottom: 16, height: 80, overflow: "hidden",
        }}>
          <div style={{
            fontSize: "0.59rem", color: "rgba(255,255,255,0.24)",
            fontFamily: "Space Grotesk, sans-serif", marginBottom: 5,
          }}>
            30-Day Performance
          </div>
          <svg width="100%" height="44" viewBox="0 0 320 44" preserveAspectRatio="none">
            <defs>
              <linearGradient id="spkGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#10B77F" stopOpacity="0.28" />
                <stop offset="100%" stopColor="#10B77F" stopOpacity="0"    />
              </linearGradient>
            </defs>
            <path
              d="M0,44 L0,38 C18,32 35,35 55,28 C75,21 92,25 112,18 C132,11 150,15 170,9 C190,4 210,7 230,4 C250,2 270,4 295,2 L320,1 L320,44 Z"
              fill="url(#spkGrad)"
            />
            <path
              d="M0,38 C18,32 35,35 55,28 C75,21 92,25 112,18 C132,11 150,15 170,9 C190,4 210,7 230,4 C250,2 270,4 295,2 L320,1"
              fill="none" stroke="#10B77F" strokeWidth="1.8" strokeLinecap="round"
            />
            <circle cx="320" cy="1" r="3.5" fill="#10B77F" />
            <circle cx="320" cy="1" r="7"   fill="#10B77F" fillOpacity="0.2" />
          </svg>
        </div>

        {/* Holdings */}
        <div style={{ marginBottom: 14 }}>
          <div style={{
            fontSize: "0.59rem", color: "rgba(255,255,255,0.24)",
            fontFamily: "Space Grotesk, sans-serif",
            textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 9,
          }}>
            Holdings
          </div>
          {HOLDINGS.map(({ ticker, pct, value, color }) => (
            <div key={ticker} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
              <span style={{
                width: 38, fontSize: "0.71rem", fontWeight: 700,
                color: "#F0EEF6", fontFamily: "Space Grotesk, sans-serif",
              }}>
                {ticker}
              </span>
              <div style={{
                flex: 1, height: 3, borderRadius: 99,
                background: "rgba(255,255,255,0.05)", overflow: "hidden",
              }}>
                <div style={{
                  height: "100%", width: `${pct}%`, borderRadius: 99,
                  background: color,
                  boxShadow: `0 0 8px ${color}70`,
                }} />
              </div>
              <span style={{
                fontSize: "0.67rem", color: "rgba(255,255,255,0.4)",
                fontFamily: "Space Grotesk, sans-serif", width: 36, textAlign: "right",
              }}>
                {pct}%
              </span>
            </div>
          ))}
        </div>

        {/* AI signals */}
        <div>
          <div style={{
            display: "flex", alignItems: "center", gap: 6, marginBottom: 8,
            fontSize: "0.59rem", color: "rgba(255,255,255,0.24)",
            fontFamily: "Space Grotesk, sans-serif",
            textTransform: "uppercase", letterSpacing: "0.12em",
          }}>
            <span style={{
              width: 5, height: 5, borderRadius: "50%", background: "#10B77F",
              animation: "pulseGold 1.8s ease-in-out infinite",
            }} />
            AI Signals · Live
          </div>
          {AI_SIGNALS.map(({ ticker, action, target, color }, i) => (
            <div key={ticker} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "5px 0",
              borderBottom: i < AI_SIGNALS.length - 1 ? "1px solid rgba(255,255,255,0.035)" : "none",
            }}>
              <span style={{
                width: 40, fontSize: "0.73rem", fontWeight: 700,
                color: "#F0EEF6", fontFamily: "Space Grotesk, sans-serif",
              }}>
                {ticker}
              </span>
              <span style={{
                fontSize: "0.59rem", fontWeight: 700, letterSpacing: "0.05em",
                padding: "2px 8px", borderRadius: 999,
                background: color + "18", border: `1px solid ${color}32`, color,
              }}>
                {action}
              </span>
              <span style={{
                fontSize: "0.72rem", fontWeight: 700, color: "#10B77F",
                fontFamily: "Space Grotesk, sans-serif",
              }}>
                {target}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Floating accent card ──────────────────────────────────────────────────────

function FloatingCard({ icon, label, value, valueColor, rotation = "-3deg", delay = "0s", position }) {
  return (
    <div style={{
      position: "absolute",
      ...position,
      zIndex: 10,
      animation: `floatCard 5s ease-in-out infinite`,
      animationDelay: delay,
      "--fc-r": rotation,
    }}>
      <div style={{
        background: "rgba(8,10,18,0.92)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 14,
        padding: "10px 16px",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        boxShadow: "0 20px 50px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)",
        display: "flex", alignItems: "center", gap: 11,
        whiteSpace: "nowrap",
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: `${valueColor}12`,
          border: `1px solid ${valueColor}22`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "1.05rem", flexShrink: 0,
        }}>
          {icon}
        </div>
        <div>
          <div style={{
            fontSize: "0.65rem", color: "rgba(255,255,255,0.32)",
            fontFamily: "Space Grotesk, sans-serif", marginBottom: 2,
          }}>
            {label}
          </div>
          <div style={{
            fontSize: "0.94rem", fontWeight: 800,
            color: valueColor, fontFamily: "Space Grotesk, sans-serif",
          }}>
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Hero Section ──────────────────────────────────────────────────────────────

export default function HeroSection() {
  const { ref: tiltRef, tilt, onMove, onLeave } = useTilt(8);
  const scrollY = useScrollY();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <section style={{
      position: "relative",
      minHeight: "100vh",
      display: "flex", alignItems: "center",
      padding: "110px 24px 80px",
      overflow: "hidden",
    }}>

      {/* ── Background layers ─────────────────────────────── */}

      {/* Dot grid with parallax */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "radial-gradient(rgba(255,255,255,0.032) 1px, transparent 1px)",
        backgroundSize: "30px 30px",
        transform: `translateY(${scrollY * 0.12}px)`,
      }} />

      {/* Green blob top-right */}
      <div style={{
        position: "absolute", top: -200, right: "5%", pointerEvents: "none",
        width: 650, height: 650, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(16,183,127,0.1) 0%, transparent 65%)",
        filter: "blur(50px)",
        transform: `translateY(${scrollY * 0.18}px)`,
      }} />

      {/* Blue blob bottom-left */}
      <div style={{
        position: "absolute", bottom: "5%", left: "-8%", pointerEvents: "none",
        width: 500, height: 500, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(59,130,246,0.07) 0%, transparent 65%)",
        filter: "blur(50px)",
        transform: `translateY(${-scrollY * 0.1}px)`,
      }} />

      {/* Purple blob center */}
      <div style={{
        position: "absolute", top: "40%", left: "40%", pointerEvents: "none",
        width: 400, height: 400, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(139,92,246,0.04) 0%, transparent 65%)",
        filter: "blur(60px)",
      }} />

      {/* Bottom fade to page bg */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, height: 140,
        background: "linear-gradient(to bottom, transparent, #06080F)",
        pointerEvents: "none",
      }} />

      {/* ── Content ─────────────────────────────────────── */}
      <div style={{
        maxWidth: 1200, width: "100%", margin: "0 auto",
        display: "grid", gridTemplateColumns: "1fr 1.05fr",
        gap: "clamp(32px, 5vw, 72px)",
        alignItems: "center",
        position: "relative", zIndex: 1,
      }} className="hero-grid">

        {/* ── Left: Text ─────────────────────────────────── */}
        <div>
          {/* Badge */}
          <div
            className="badge badge-gold"
            style={{
              marginBottom: 28, display: "inline-flex", gap: 8,
              opacity: mounted ? 1 : 0,
              transform: mounted ? "translateY(0)" : "translateY(12px)",
              transition: "opacity 0.6s ease 0.05s, transform 0.6s ease 0.05s",
            }}
          >
            <span style={{
              width: 6, height: 6, borderRadius: "50%",
              background: "var(--gold)", flexShrink: 0,
              animation: "pulseGold 2s ease infinite",
            }} />
            Early Access · Launching April 2026
          </div>

          {/* Headline */}
          <h1 style={{
            fontFamily: "Space Grotesk, sans-serif",
            fontSize: "clamp(2.4rem, 4.2vw, 3.7rem)",
            fontWeight: 900, lineHeight: 1.07, letterSpacing: "-0.035em",
            color: "#F0EEF6", marginBottom: 22,
          }}>
            <span className="line-wrap">
              <span className="line-reveal" style={{ animationDelay: "0.08s" }}>
                Invest with the
              </span>
            </span>
            <span className="line-wrap">
              <span className="line-reveal" style={{ animationDelay: "0.2s" }}>
                <span className="gold-text shimmer-text">
                  <TypewriterText text="clarity of AI." />
                </span>
              </span>
            </span>
          </h1>

          {/* Description */}
          <p
            className="anim-fade-up d3"
            style={{
              fontSize: "1.05rem", color: "rgba(240,238,246,0.52)",
              lineHeight: 1.78, marginBottom: 40,
              fontFamily: "Space Grotesk, sans-serif",
              maxWidth: 490,
            }}
          >
            Alpha Stack brings institutional-grade AI analysis to retail investors.
            Real-time signals, portfolio analytics, and macro intelligence —
            all in one focused dashboard.
          </p>

          {/* Form */}
          <div id="hero-form" className="anim-fade-up d4" style={{ marginBottom: 28 }}>
            <WaitlistForm variant="hero" />
          </div>

          {/* Social proof */}
          <div
            className="anim-fade-in d5"
            style={{ display: "flex", alignItems: "center", gap: 14 }}
          >
            <div style={{ display: "flex" }}>
              {["#10B77F", "#60A5FA", "#A78BFA", "#FBBF24", "#F87171"].map((c, i) => (
                <div key={c} style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: `linear-gradient(135deg, ${c}55, ${c}18)`,
                  border: "2.5px solid #06080F",
                  marginLeft: i > 0 ? -9 : 0,
                  flexShrink: 0,
                }} />
              ))}
            </div>
            <span style={{
              fontSize: "0.84rem", color: "rgba(240,238,246,0.42)",
              fontFamily: "Space Grotesk, sans-serif",
            }}>
              <strong style={{ color: "rgba(240,238,246,0.82)", fontWeight: 700 }}>
                1,200+
              </strong>{" "}
              investors already on the waitlist
            </span>
          </div>
        </div>

        {/* ── Right: 3D dashboard ─────────────────────────── */}
        <div
          className="hero-mockup-col anim-fade-in d3"
          ref={tiltRef}
          onMouseMove={onMove}
          onMouseLeave={onLeave}
          style={{
            position: "relative",
            perspective: "1400px",
            perspectiveOrigin: "center center",
          }}
        >
          {/* 3D tilting wrapper */}
          <div style={{
            transform: `
              rotateX(${tilt.x}deg)
              rotateY(${tilt.y}deg)
              translateY(${-scrollY * 0.06}px)
            `,
            transition: "transform 0.28s cubic-bezier(0.16,1,0.3,1)",
            transformStyle: "preserve-3d",
          }}>
            <DashboardWindow />
          </div>

          {/* Floating card: monthly return */}
          <FloatingCard
            icon="📈"
            label="Monthly Return"
            value="+18.4%"
            valueColor="#10B77F"
            rotation="-4deg"
            delay="0s"
            position={{ top: -22, right: -28 }}
          />

          {/* Floating card: AI signals */}
          <FloatingCard
            icon="⚡"
            label="AI Signals Today"
            value="94 Active"
            valueColor="#60A5FA"
            rotation="3deg"
            delay="1.8s"
            position={{ bottom: 32, left: -32 }}
          />

          {/* Subtle glow beneath mockup */}
          <div style={{
            position: "absolute", bottom: -60, left: "50%",
            transform: "translateX(-50%)",
            width: "80%", height: 80, pointerEvents: "none",
            background: "radial-gradient(ellipse, rgba(16,183,127,0.2) 0%, transparent 70%)",
            filter: "blur(28px)",
          }} />
        </div>
      </div>
    </section>
  );
}
